/**
 * UserDropdown Component
 * Dropdown menu for user actions with member and admin sections
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import './UserDropdown.css';

interface UserDropdownProps {
  onLogout?: () => void;
}

/**
 * Check if user has admin privileges (moderator, admin, or super_admin)
 */
const hasAdminPrivileges = (user: { role: string } | null): boolean => {
  if (!user) return false;
  return ['moderator', 'admin', 'super_admin'].includes(user.role);
};

/**
 * Admin menu items configuration
 */
const getAdminMenuItems = (user: { role: string } | null) => {
  const items = [
    { path: '/admin', label: '管理後台', icon: '⚙️' },
    { path: '/admin/queue', label: '待審廣告', icon: '📋' },
    { path: '/admin/content', label: '內容管理', icon: '📝' },
    { path: '/admin/marquees', label: '跑馬燈管理', icon: '📢' },
  ];

  if (user?.role === 'admin' || user?.role === 'super_admin') {
    items.push({ path: '/admin/members', label: '會員管理', icon: '👥' });
  }

  if (user?.role === 'super_admin') {
    items.push({ path: '/admin/system/bootstrap', label: 'Bootstrap 工具', icon: '🔧' });
  }

  return items;
};

/**
 * UserDropdown Component
 */
export const UserDropdown: React.FC<UserDropdownProps> = ({ onLogout }) => {
  const { user, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  /**
   * Handle click outside to close dropdown
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        triggerRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /**
   * Handle keyboard navigation
   */
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Escape' && isOpen) {
      setIsOpen(false);
      triggerRef.current?.focus();
    }
    if (event.key === 'Tab' && isOpen) {
      // Allow tab navigation but close dropdown on blur
      setTimeout(() => {
        if (document.activeElement?.closest('.user-dropdown') === null) {
          setIsOpen(false);
        }
      }, 0);
    }
  }, [isOpen]);

  /**
   * Toggle dropdown
   */
  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  /**
   * Handle logout
   */
  const handleLogout = () => {
    logout();
    setIsOpen(false);
    onLogout?.();
  };

  /**
   * Handle menu item click
   */
  const handleMenuItemClick = () => {
    setIsOpen(false);
  };

  if (!user) {
    return null;
  }

  const adminMenuItems = getAdminMenuItems(user);
  const showAdminSection = hasAdminPrivileges(user);

  // Get role badge text
  const getRoleBadge = () => {
    switch (user?.role) {
      case 'super_admin':
        return { text: '超級管理員', className: 'user-dropdown__badge--super-admin' };
      case 'admin':
        return { text: '管理員', className: 'user-dropdown__badge--admin' };
      case 'moderator':
        return { text: '版主', className: 'user-dropdown__badge--moderator' };
      default:
        return null;
    }
  };

  const roleBadge = getRoleBadge();

  return (
    <div className="user-dropdown" onKeyDown={handleKeyDown}>
      {/* Trigger Button */}
      <button
        ref={triggerRef}
        className="user-dropdown__trigger"
        onClick={toggleDropdown}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label="使用者選單"
      >
        <span className="user-dropdown__user-name">{user.name}</span>
        {roleBadge && (
          <span className={`user-dropdown__badge ${roleBadge.className}`}>
            {roleBadge.text}
          </span>
        )}
        <svg
          className={`user-dropdown__arrow ${isOpen ? 'user-dropdown__arrow--open' : ''}`}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3 4.5L6 7.5L9 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="user-dropdown__menu"
          ref={dropdownRef}
          role="menu"
          aria-orientation="vertical"
          aria-labelledby="user-dropdown-trigger"
        >
          {/* Member Section */}
          <div className="user-dropdown__section" role="group" aria-label="會員選項">
            <a
              href="/member"
              className="user-dropdown__item"
              role="menuitem"
              tabIndex={-1}
              onClick={handleMenuItemClick}
            >
              <span className="user-dropdown__item-icon">🏠</span>
              <span className="user-dropdown__item-label">會員中心</span>
            </a>
            <a
              href="/help"
              className="user-dropdown__item"
              role="menuitem"
              tabIndex={-1}
              onClick={handleMenuItemClick}
            >
              <span className="user-dropdown__item-icon">📖</span>
              <span className="user-dropdown__item-label">操作手冊</span>
            </a>
          </div>

          {/* Admin Section */}
          {showAdminSection && (
            <>
              <div className="user-dropdown__divider" role="separator" />
              <div className="user-dropdown__section" role="group" aria-label="管理選項">
                {adminMenuItems.map((item) => (
                  <a
                    key={item.path}
                    href={item.path}
                    className="user-dropdown__item"
                    role="menuitem"
                    tabIndex={-1}
                    onClick={handleMenuItemClick}
                  >
                    <span className="user-dropdown__item-icon">{item.icon}</span>
                    <span className="user-dropdown__item-label">{item.label}</span>
                  </a>
                ))}
              </div>
            </>
          )}

          {/* Bottom Section */}
          <div className="user-dropdown__divider" role="separator" />
          <div className="user-dropdown__section" role="group" aria-label="其他選項">
            <button
              className="user-dropdown__item user-dropdown__item--logout"
              role="menuitem"
              tabIndex={-1}
              onClick={handleLogout}
            >
              <span className="user-dropdown__item-icon">🚪</span>
              <span className="user-dropdown__item-label">登出</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDropdown;
